import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { adminPutSettingsGeneral } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useSiteSettings } from "../context/SiteSettingsContext";

const HEIGHT_VAR = "--site-announcement-height";
const NAV_HEIGHT_VAR = "--site-nav-height";

type Props = {
  /** When true, skip fixed positioning (coming-soon flow stacks naturally). */
  inline?: boolean;
};

/**
 * Site-wide IMPORTANT ANNOUNCEMENT strip. Independent of event cancel banners
 * and of the featured overnight title/date.
 */
export function SiteAnnouncementBanner({ inline = false }: Props) {
  const { settings, setSettings } = useSiteSettings();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("settings");
  const ref = useRef<HTMLElement | null>(null);
  const formId = useId();
  const enabled = settings.announcementEnabled;
  const title = settings.announcementTitle?.trim() || "IMPORTANT ANNOUNCEMENT";
  const body = settings.announcementBody?.trim() || "";

  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title);
  const [draftBody, setDraftBody] = useState(body);
  const [draftEnabled, setDraftEnabled] = useState(enabled);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) {
      setDraftTitle(title);
      setDraftBody(body);
      setDraftEnabled(enabled);
      setError(null);
    }
  }, [title, body, enabled, editing]);

  useEffect(() => {
    if (inline) return;
    const root = document.documentElement;
    const banner = ref.current;
    const nav = document.querySelector<HTMLElement>(".nav");

    const apply = () => {
      const bannerH =
        enabled && banner ? Math.ceil(banner.getBoundingClientRect().height) : 0;
      const navH = nav ? Math.ceil(nav.getBoundingClientRect().height) : 88;
      root.style.setProperty(HEIGHT_VAR, `${bannerH}px`);
      root.style.setProperty(NAV_HEIGHT_VAR, `${navH}px`);
      root.classList.toggle("has-site-announcement", bannerH > 0);
    };

    apply();
    const ro = new ResizeObserver(apply);
    if (banner) ro.observe(banner);
    if (nav) ro.observe(nav);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
      root.style.setProperty(HEIGHT_VAR, "0px");
      root.classList.remove("has-site-announcement");
    };
  }, [enabled, title, body, inline, editing]);

  async function save() {
    const nextTitle = draftTitle.trim() || "IMPORTANT ANNOUNCEMENT";
    const nextBody = draftBody.trim();
    if (!nextBody) {
      setError("Body is required to show the banner.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = await adminPutSettingsGeneral({
        announcementEnabled: draftEnabled,
        announcementTitle: nextTitle,
        announcementBody: nextBody,
      });
      setSettings(next);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save announcement");
    } finally {
      setBusy(false);
    }
  }

  if (!enabled || !body) return null;

  return (
    <aside
      ref={ref}
      className={`site-announcement-banner${inline ? " site-announcement-banner--inline" : ""}`}
      role="status"
      aria-live="polite"
    >
      <div className="site-announcement-inner">
        {editing ? (
          <form
            className="site-announcement-edit"
            aria-labelledby={`${formId}-label`}
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
          >
            <div id={`${formId}-label`} className="site-announcement-k">
              Edit Important Updates
            </div>
            <label className="site-announcement-edit-toggle">
              <input
                type="checkbox"
                checked={draftEnabled}
                disabled={busy}
                onChange={(e) => setDraftEnabled(e.target.checked)}
              />
              <span>Show banner</span>
            </label>
            <input
              className="site-announcement-edit-title"
              value={draftTitle}
              disabled={busy}
              maxLength={120}
              aria-label="Announcement title"
              onChange={(e) => setDraftTitle(e.target.value)}
            />
            <textarea
              className="site-announcement-edit-body"
              value={draftBody}
              disabled={busy}
              rows={3}
              maxLength={2000}
              aria-label="Announcement body"
              onChange={(e) => setDraftBody(e.target.value)}
            />
            {error && <p className="site-announcement-edit-error">{error}</p>}
            <div className="site-announcement-edit-actions">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
              <Link className="site-announcement-edit-link" to="/admin/settings/general">
                Open Settings
              </Link>
            </div>
          </form>
        ) : (
          <>
            <div className="site-announcement-k">{title}</div>
            <p className="site-announcement-body">{body}</p>
            {canEdit && (
              <button
                type="button"
                className="site-announcement-pencil"
                aria-label="Edit Important Updates"
                title="Edit Important Updates"
                onClick={() => setEditing(true)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
                  />
                </svg>
              </button>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
